/**
 * The expanded half of a provider card: authentication options and install fields.
 *
 * Split from `ProviderCard` on a complete JSX boundary -- everything inside
 * `<CollapsibleContent>`. An earlier attempt cut at the `isComingSoon` branch,
 * which sits mid-ternary and left unbalanced tags on both sides; the chain here
 * is one expression and has to move as one.
 *
 * The branching is the content: Codex and Claude get real auth components,
 * coming-soon providers get a sentence, and the rest get install fields that
 * differ by whether the provider is reached over HTTP or as a local binary.
 *
 * @module
 */
import { DEFAULT_APEX_PROFILES } from "@cmmd-forge/contracts/settings";
import type { UnifiedSettings } from "@cmmd-forge/contracts/settings";
import { InfoIcon } from "lucide-react";

import { isElectron } from "../../env";
import { useClaudeCloudSignIn } from "../../hooks/useClaudeCloudSignIn";
import type { useUpdateSettings } from "../../hooks/useSettings";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { ClaudeAuthenticationOptions } from "./ClaudeAuthenticationOptions";
import { CodexAuthenticationOptions } from "./CodexAuthenticationOptions";
import type { CmmdProviderAccountInventoryAccount } from "./providerAccountState";
import type { ProviderCardModel } from "./providerCardModel";
import { ProviderModelsList } from "./ProviderModelsList";
import { formatProviderProvenanceDetails, isDebugDisclosureEnabled } from "./providerProvenance";

export interface ProviderCardDetailsProps {
  readonly providerCard: ProviderCardModel;
  readonly providerDisplayName: string;
  readonly provenanceDetails: ReturnType<typeof formatProviderProvenanceDetails>;
  readonly settings: UnifiedSettings;
  readonly updateSettings: ReturnType<typeof useUpdateSettings>["updateSettings"];
  readonly codexProviderAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly claudeProviderAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly codexHomePath: string;
  readonly codexCloudSignIn: Parameters<typeof CodexAuthenticationOptions>[0]["cloudSignIn"];
  readonly claudeCloudSignIn: ReturnType<typeof useClaudeCloudSignIn>["controller"];
  readonly claudeCloudActive: ReturnType<typeof useClaudeCloudSignIn>["active"];
  readonly workspaceWalletAllowed: boolean;
  readonly refreshProviders: () => void;
}

// Moved verbatim: 5377-line SettingsPanels.tsx -> ProviderSettingsPanel ->
// here. Complexity is unchanged by the relocation; fallow reads a moved
// function as a new one. The original carried this same suppression.
// fallow-ignore-next-line complexity
export function ProviderCardDetails({
  providerCard,
  providerDisplayName,
  provenanceDetails,
  settings,
  updateSettings,
  codexProviderAccounts,
  claudeProviderAccounts,
  codexHomePath,
  codexCloudSignIn,
  claudeCloudSignIn,
  claudeCloudActive,
  workspaceWalletAllowed,
  refreshProviders,
}: ProviderCardDetailsProps) {
  return (
    <div className="space-y-6 px-3 pb-5 pt-3 sm:px-4">
      {providerCard.provider === "codex" && !isDebugDisclosureEnabled() ? (
        <CodexAuthenticationOptions
          provider={providerCard.liveProvider}
          accessMode={settings.providers.codex.accessMode}
          cloudSignIn={codexCloudSignIn}
          providerAccounts={codexProviderAccounts}
          workspaceWalletAllowed={workspaceWalletAllowed}
          onAccessModeChange={(accessMode) => {
            updateSettings({
              providers: {
                ...settings.providers,
                codex: {
                  ...settings.providers.codex,
                  accessMode,
                },
              },
            });
          }}
          onAuthCompleted={refreshProviders}
        />
      ) : providerCard.provider === "claudeAgent" && !isDebugDisclosureEnabled() ? (
        <ClaudeAuthenticationOptions
          provider={providerCard.liveProvider}
          settings={settings}
          updateSettings={updateSettings}
          cloudSignIn={claudeCloudSignIn}
          claudeCloudActive={claudeCloudActive}
          providerAccounts={claudeProviderAccounts}
        />
      ) : providerCard.isComingSoon ? (
        <div className="border-t border-border/60 px-4 py-3 text-xs text-muted-foreground sm:px-5">
          This provider will be enabled after the Codex-first S1 path is stable.
        </div>
      ) : isElectron ? (
        providerCard.kind === "http" ? (
          <div className="min-w-0">
            <label htmlFor={`provider-install-${providerCard.provider}-base-url`} className="block">
              <span className="text-xs font-medium text-foreground">
                {providerDisplayName} base URL
              </span>
              <Input
                id={`provider-install-${providerCard.provider}-base-url`}
                className="mt-1.5"
                value={providerCard.endpointUrl}
                onChange={(event) =>
                  updateSettings({
                    providers: {
                      ...settings.providers,
                      [providerCard.provider]: {
                        ...settings.providers[providerCard.provider],
                        baseUrl: event.target.value,
                      },
                    },
                  })
                }
                placeholder={providerCard.endpointPlaceholder}
                spellCheck={false}
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                {providerCard.endpointDescription}
              </span>
            </label>
          </div>
        ) : (
          <>
            <div className="min-w-0">
              <label
                htmlFor={`provider-install-${providerCard.provider}-binary-path`}
                className="block"
              >
                <span className="text-xs font-medium text-foreground">
                  {providerDisplayName} binary path
                </span>
                <Input
                  id={`provider-install-${providerCard.provider}-binary-path`}
                  className="mt-1.5"
                  value={providerCard.binaryPathValue}
                  onChange={(event) =>
                    updateSettings({
                      providers: {
                        ...settings.providers,
                        [providerCard.provider]: {
                          ...settings.providers[providerCard.provider],
                          binaryPath: event.target.value,
                        },
                      },
                    })
                  }
                  placeholder={providerCard.binaryPlaceholder}
                  spellCheck={false}
                />
                <span className="mt-1 block text-xs text-muted-foreground">
                  {providerCard.binaryDescription}
                </span>
              </label>
            </div>

            {providerCard.homePathKey ? (
              <div className="min-w-0">
                <label htmlFor={`provider-install-${providerCard.homePathKey}`} className="block">
                  <span className="text-xs font-medium text-foreground">CODEX_HOME path</span>
                  <Input
                    id={`provider-install-${providerCard.homePathKey}`}
                    className="mt-1.5"
                    value={codexHomePath}
                    onChange={(event) =>
                      updateSettings({
                        providers: {
                          ...settings.providers,
                          codex: {
                            ...settings.providers.codex,
                            homePath: event.target.value,
                          },
                        },
                      })
                    }
                    placeholder={providerCard.homePlaceholder}
                    spellCheck={false}
                  />
                  {providerCard.homeDescription ? (
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {providerCard.homeDescription}
                    </span>
                  ) : null}
                </label>
              </div>
            ) : null}
          </>
        )
      ) : null}

      {providerCard.provider === "apex" && isDebugDisclosureEnabled() ? (
        <div className="min-w-0">
          <div className="text-xs font-medium text-foreground">Apex Models</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {DEFAULT_APEX_PROFILES.length} default profile
            {DEFAULT_APEX_PROFILES.length === 1 ? "" : "s"}.
          </div>
          <div className="mt-2 space-y-1">
            {DEFAULT_APEX_PROFILES.map((profile) => {
              const profileKey = profile.displayName ?? profile.slug;
              const isDisabled = (settings.providers.apex.disabledProfiles ?? []).includes(
                profileKey,
              );
              return (
                <div key={profileKey} className="flex items-center justify-between gap-2 py-1">
                  <div className="min-w-0 flex-1">
                    <span className="text-xs text-foreground/90">
                      {profile.displayName ?? profile.slug}
                    </span>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <button
                            type="button"
                            className="ml-1.5 shrink-0 text-muted-foreground/40 transition-colors hover:text-muted-foreground"
                            aria-label={`Details for ${profileKey}`}
                          />
                        }
                      >
                        <InfoIcon className="size-3" />
                      </TooltipTrigger>
                      <TooltipPopup side="top" className="max-w-56">
                        <span className="text-[11px] text-muted-foreground">
                          {profile.description ?? profile.displayName ?? "Apex profile"}
                        </span>
                      </TooltipPopup>
                    </Tooltip>
                  </div>
                  <Switch
                    checked={!isDisabled}
                    onCheckedChange={(checked) => {
                      const currentDisabled = settings.providers.apex.disabledProfiles ?? [];
                      const nextDisabled = checked
                        ? currentDisabled.filter((name) => name !== profileKey)
                        : [...currentDisabled, profileKey];
                      updateSettings({
                        providers: {
                          ...settings.providers,
                          apex: {
                            ...settings.providers.apex,
                            disabledProfiles: nextDisabled,
                          },
                        },
                      });
                    }}
                    aria-label={`Enable ${profileKey}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {provenanceDetails.length > 0 ? (
        <div className="min-w-0">
          <div className="text-xs font-medium text-foreground">Authentication provenance</div>
          <dl className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
            {provenanceDetails.map(([label, value]) => (
              <div key={label} className="min-w-0">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="truncate font-mono text-foreground/80">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}

      <ProviderModelsList provider={providerCard.provider} models={providerCard.models} />
    </div>
  );
}
